import { memo } from "react";

import { ERRORED_CLASSNAME } from "@impero-com/common/utils/form/erroredElements";
import { FormValidationError } from "@impero-com/common/utils/form/formAtom/types";
import { FieldError, Svg } from "@impero-com/ui";
import { Typography } from "antd";
import { cx } from "class-variance-authority";

import { Field } from "components/fieldBuilder/models";
import * as fieldBuilderTheme from "components/fieldBuilder/theme.css";
import conditionalDependencyBrokenSvg from "img/conditional-dependency-broken.svg";
import conditionalDependencySvg from "img/conditional-dependency.svg";
import conditionalLogicActivatedSvg from "img/conditional-logic-activated.svg";

import * as sharedTheme from "./theme.css";

const { Text } = Typography;

function checkHasDependantFields(
    fields: Field<unknown, string>[],
    field: Field<unknown, string>
) {
    return fields.some(
        (otherField) => otherField.condition?.fieldId === field.id
    );
}

interface FieldCmpProps {
    fields: Field<unknown, string>[];
    field: Field<unknown, string>;
    error: FormValidationError | undefined;
    openDrawer: () => void;
    disabled?: boolean;
    svgSrc: string;
}

export const FieldCmp = memo(
    ({
        openDrawer,
        fields,
        error,
        field,
        svgSrc,
        disabled = false,
    }: FieldCmpProps) => {
        const hasCondition = field.condition !== null;
        const hasConditionValidationError = error?.yup.path === "condition";
        const hasDependantFields = checkHasDependantFields(fields, field);

        return (
            <div
                className={cx(
                    error ? sharedTheme.fieldError : fieldBuilderTheme.field,
                    error && ERRORED_CLASSNAME,
                    !disabled && sharedTheme.hover,
                    !disabled ? sharedTheme.draggable : sharedTheme.clickable
                )}
                onClick={() => {
                    openDrawer();
                }}
            >
                <div className={sharedTheme.fieldCmpRoot}>
                    <Svg src={svgSrc} className={fieldBuilderTheme.fieldIcon} />
                    <div className={sharedTheme.fieldCmpContent}>
                        <Text ellipsis={{ tooltip: field.title }}>
                            {field.title}
                        </Text>
                    </div>
                    <div className={sharedTheme.fieldCmpConditionIcons}>
                        {hasDependantFields && (
                            <Svg src={conditionalLogicActivatedSvg} />
                        )}
                        {hasConditionValidationError && (
                            <Svg src={conditionalDependencyBrokenSvg} />
                        )}
                        {hasCondition && !hasConditionValidationError && (
                            <Svg src={conditionalDependencySvg} />
                        )}
                    </div>
                </div>
                {error && <div className={sharedTheme.fieldErrorSeparator} />}
                <FieldError error={error?.value} />
            </div>
        );
    },
    (prevProps, nextProps) => {
        const areEq =
            prevProps.disabled === nextProps.disabled &&
            prevProps.error === nextProps.error &&
            Object.is(prevProps.field, nextProps.field) &&
            Object.is(prevProps.field.condition, nextProps.field.condition) &&
            checkHasDependantFields(prevProps.fields, prevProps.field) ===
                checkHasDependantFields(nextProps.fields, nextProps.field);

        return areEq;
    }
);
